import {
  createApp,
  configureCompat,
  defineAsyncComponent,
  h,
  Teleport,
} from 'vue';
import { createI18n } from 'vue-i18n';
import axios from 'axios';
import '../dashboard/assets/scss/app.scss';
// Global Components
import OtelloSwitch from 'components/ui/Switch';
import OtelloWizard from 'components/ui/Wizard';
import { vuelidateBridge } from '../dashboard/mixins/vuelidateBridge';
import OtelloUiKit from '../dashboard/components';
import App from '../dashboard/App';
import i18n, {
  DEFAULT_LOCALE,
  loadLocaleMessages,
  normalizeLocale,
} from '../dashboard/i18n';
import { setI18nInstance } from '../dashboard/composables/useI18n';
import createAxios from '../dashboard/helper/APIHelper';
import { emitter } from '../shared/helpers/mitt';

import commonHelpers from '../dashboard/helper/commons';
import router, { initalizeRouter } from '../dashboard/routes';
import store from '../dashboard/store';
import { syncRouteToStore } from '../dashboard/helper/routeStoreSync';
import { startPwaInstallListener } from '../dashboard/helper/pwaInstall';
import constants from 'dashboard/constants/globals';
import { checkAppVersion } from '../dashboard/helper/versionCheck';
import resizeDirective from '../dashboard/helper/directives/resize.js';
import dompurifyHtmlDirective from '../shared/directives/dompurifyHtml';
import { directive as onClickaway } from '../dashboard/helper/directives/clickOutside';
import tooltipDirective from '../dashboard/helper/directives/tooltip';
import { installConsoleRedactor } from '../dashboard/helper/consoleRedactor';
import { initializeSentry } from '../dashboard/helper/sentry';
import {
  addSafeDocumentEventListener,
  addSafeWindowEventListener,
} from '../dashboard/helper/domEvents';
import { setWindowGlobal } from '../dashboard/helper/browserRuntime';
import { getOtelloConfigValue } from '../shared/helpers/runtimeConfig';

// Otello da mobile: la richiesta "Installa l'app" di Chrome arriva una volta
// sola al caricamento: va raccolta subito.
startPwaInstallListener();

// Vue 3 migration build: keep Vue 2 component-level behaviour app-wide while
// the codebase is migrated area by area (B2).
// MODE 2 keeps Vue 2 compat for templates/options API, but RENDER_FUNCTION
// compat wraps every JS render() as a legacy Vue 2 render (compatRender),
// which breaks render functions written in / migrated to Vue 3 h() (Tabs,
// ChatListPendingTracker, ...). Treat JS render functions as Vue 3.
//
// ATTR_* and WATCH_ARRAY are still intentionally running in compat mode while
// the migration is incremental. Keep the legacy behaviour but stop emitting the
// same migration warnings on every app boot.
configureCompat({
  MODE: 2,
  RENDER_FUNCTION: false,
  ATTR_ENUMERATED_COERCION: 'suppress-warning',
  ATTR_FALSE_VALUE: 'suppress-warning',
  WATCH_ARRAY: 'suppress-warning',
});

installConsoleRedactor();

const initialLocale = normalizeLocale(
  getOtelloConfigValue('selectedLocale', DEFAULT_LOCALE)
);
const i18nConfig = createI18n({
  legacy: true,
  globalInjection: true,
  locale: initialLocale,
  fallbackLocale: DEFAULT_LOCALE,
  messages: i18n,
});
setI18nInstance(i18nConfig);

// load common helpers into js
commonHelpers();

setWindowGlobal('OtelloConstants', constants);
setWindowGlobal('axios', createAxios(axios));

syncRouteToStore(store, router);
initalizeRouter();

const bootApplication = async () => {
  const app = createApp({
    render: () => h(App),
  });

  const sentryReady = initializeSentry(app);

  app.use(router);
  app.use(store);
  app.use(i18nConfig);
  app.use(OtelloUiKit);
  app.mixin(vuelidateBridge);

  app.component('otello-switch', OtelloSwitch);
  app.component('otello-wizard', OtelloWizard);
  app.component(
    'fluent-icon',
    defineAsyncComponent(
      () =>
        import(
          /* webpackChunkName: "fluent-icon" */ 'shared/components/FluentIcon/DashboardIcon'
        )
    )
  );
  app.component('Teleport', Teleport);
  app.component('teleport', Teleport);

  app.directive('resize', resizeDirective);
  app.directive('dompurify-html', dompurifyHtmlDirective);
  app.directive('on-clickaway', onClickaway);
  app.directive('tooltip', tooltipDirective);

  app.config.globalProperties.$emitter = emitter;

  await Promise.all([
    sentryReady,
    loadLocaleMessages(i18nConfig, initialLocale),
  ]);
  const shouldStopBoot = await checkAppVersion();
  if (shouldStopBoot) return;

  // app.mount() returns the root component public instance (has $store via
  // globalProperties); the Vue 3 app object itself does NOT. actionCable and
  // other consumers read OTELLO_APP.$store, so expose the mounted root (the
  // Vue 2 `new Vue().$mount()` equivalent).
  const rootInstance = app.mount('#app');
  setWindowGlobal('OTELLO_APP', rootInstance);
};

let hasBooted = false;
const bootApplicationOnce = () => {
  if (hasBooted) return;
  hasBooted = true;
  bootApplication();
};

if (document.readyState === 'loading') {
  addSafeDocumentEventListener('DOMContentLoaded', bootApplicationOnce, {
    once: true,
  });
  addSafeWindowEventListener('load', bootApplicationOnce, { once: true });
} else {
  bootApplicationOnce();
}
